import React from 'react';
import ENDPOINT from '../constants/endpoints';
import LinkBuilder from '../helpers/link-builder';
import StringHelpers from '../helpers/string-helpers';
import {Permission} from '../helpers/user';
import useApi from '../hooks/useApi';
import AssignProcessDialog from '../scenes/Dialog/components/AssignProcessDialog';
import BasicDialog from '../scenes/Dialog/components/BasicDialog';
import CloseProcessDialog from '../scenes/Dialog/components/CloseProcessDialog';
import ConfirmDialog from '../scenes/Dialog/components/ConfirmDialog';
import PushProcessDialog from '../scenes/Dialog/components/PushProcessDialog';
import ResponseErrorDialog from '../scenes/Dialog/components/ResponseErrorDialog';
import UpgradeRecruiterAccount from '../scenes/Dialog/components/UpgradeRecruiterAccount';

const TYPE = {
  PROJECT: 'project',
  JOB: 'process',
};

const withProcessActions = WrappedComponent => props => {
  const suppProps = { ...props };
  const { user, history } = props;
  const api = useApi();

  const pushProcess = (id, type) => {
    const endpoint =
      type === TYPE.JOB
        ? ENDPOINT.ACTION.MANAGE_JOBS.PUSH
        : ENDPOINT.ACTION.MANAGE_PROJECTS.PUSH;

    return new Promise(resolve => {
      PushProcessDialog({ type: StringHelpers.ucfirst(type) }).then(
        sendMail => {
          api.post(
            endpoint,
            { id, notify: sendMail ? 1 : 0 },
            {
              handleError: response => {
                ResponseErrorDialog({ response });
                resolve(false);
              },
              handleSuccess: () => resolve(true),
            }
          );
        },
        () => resolve(false)
      );
    });
  };

  const refreshProcess = (id, type) => {
    const endpoint =
      type === TYPE.JOB
        ? ENDPOINT.ACTION.MANAGE_JOBS.REFRESH
        : ENDPOINT.ACTION.MANAGE_PROJECTS.REFRESH;

    return new Promise(resolve => {
      api.post(
        endpoint,
        { id },
        {
          handleError: response => {
            ResponseErrorDialog({ response });
            resolve(false);
          },
          handleSuccess: () => {
            BasicDialog({
              bodyRender: 'Your request has been processed and will be approved by the GoBuyside team'
            });
            return resolve(true);
          },
        }
      );
    });
  };

  const closeProcess = (id, type) => {
    const endpoint =
      type === TYPE.JOB
        ? ENDPOINT.ACTION.MANAGE_JOBS.CLOSE
        : ENDPOINT.ACTION.MANAGE_PROJECTS.CLOSE;

    return new Promise(resolve => {
      CloseProcessDialog().then(
        sendMail => {
          api.post(
            endpoint,
            { id, noemail: sendMail ? 'N' : 'Y' },
            {
              handleError: response => {
                ResponseErrorDialog({ response });
                resolve(false);
              },
              handleSuccess: () => resolve(true),
            }
          );
        },
        () => resolve(false)
      );
    });
  };

  const reopenProcess = (id, type) => {
    const endpoint =
      type === TYPE.JOB
        ? ENDPOINT.ACTION.MANAGE_JOBS.REOPEN
        : ENDPOINT.ACTION.MANAGE_PROJECTS.REOPEN;

    return new Promise(resolve => {
      ConfirmDialog({
        dismissWhenClose: false,
        bodyRender: `Are you sure you want to reopen this ${type}?`,
      }).then(
        () => {
          api.post(
            endpoint,
            { id },
            {
              handleError: response => {
                ResponseErrorDialog({ response });
                resolve(false);
              },
              handleSuccess: () => resolve(true),
            }
          );
        },
        () => resolve(false)
      );
    });
  };

  const assignProcess = (id, type) => {
    const assign =
      type === TYPE.JOB
        ? ENDPOINT.ACTION.MANAGE_JOBS.ASSIGN
        : ENDPOINT.ACTION.MANAGE_PROJECTS.ASSIGN;
    const firm =
      type === TYPE.JOB
        ? ENDPOINT.LIST.MANAGE_JOBS_FIRMS_FOR_ASSIGN
        : ENDPOINT.LIST.MANAGE_PROJECTS_FIRMS_FOR_ASSIGN;

    return new Promise(resolve => {
      AssignProcessDialog({ id, type, endpoints: { firm, assign } }).then(
        response => {
          if (response) {
            ResponseErrorDialog({ response });
            resolve(false);
          } else {
            resolve(true);
          }
        }
      );
    });
  };

  const removeAssignProcess = (id, type) => {
    const endpoint =
      type === TYPE.JOB
        ? ENDPOINT.ACTION.MANAGE_JOBS.REMOVE_ASSIGN
        : ENDPOINT.ACTION.MANAGE_PROJECTS.REMOVE_ASSIGN;

    return new Promise(resolve => {
      ConfirmDialog({
        dismissWhenClose: false,
        bodyRender: `Do you want to remove assign from this ${type}?`,
      }).then(
        () => {
          api.post(
            endpoint,
            { id },
            {
              handleError: response => {
                ResponseErrorDialog({ response });
                resolve(false);
              },
              handleSuccess: () => resolve(true),
            }
          );
        },
        () => resolve(false)
      );
    });
  };

  const removeProcess = (id, type) => {
    const endpoint =
      type === TYPE.JOB
        ? ENDPOINT.ACTION.MANAGE_JOBS.DELETE
        : ENDPOINT.ACTION.MANAGE_PROJECTS.DELETE;

    return new Promise(resolve => {
      ConfirmDialog({
        dismissWhenClose: false,
        bodyRender: `Are you sure you want to delete this ${type}?`,
      }).then(
        () => {
          api.post(
            endpoint,
            { id },
            {
              handleError: response => {
                ResponseErrorDialog({ response });
                resolve(false);
              },
              handleSuccess: () => resolve(true),
            }
          );
        },
        () => resolve(false)
      );
    });
  };

  const viewProcessApplicants = (id, type) => {
    if (
      (TYPE.JOB &&
        !Permission.isAllowedProcessViewApplicants(user.permissions)) ||
      (TYPE.PROJECT &&
        !Permission.isAllowedProjectViewApplicants(user.permissions))
    ) {
      UpgradeRecruiterAccount();
    } else {
      const link =
        type === TYPE.JOB
          ? LinkBuilder.getJobCandidatesAppLink(id)
          : LinkBuilder.getProjectCandidatesAppLink(id);
      history.push(link);
    }
  };

  suppProps.processAction = {
    jobPush(id) {
      pushProcess(id, TYPE.JOB);
    },

    jobRefresh(id) {
      refreshProcess(id, TYPE.JOB);
    },

    projectPush(id) {
      pushProcess(id, TYPE.PROJECT);
    },

    projectRefresh(id) {
      refreshProcess(id, TYPE.PROJECT);
    },

    jobClose(id) {
      return closeProcess(id, TYPE.JOB);
    },
    projectClose(id) {
      return closeProcess(id, TYPE.PROJECT);
    },

    jobReopen(id) {
      return reopenProcess(id, TYPE.JOB);
    },
    projectReopen(id) {
      return reopenProcess(id, TYPE.PROJECT);
    },

    jobAssign(id) {
      return assignProcess(id, TYPE.JOB);
    },
    projectAssign(id) {
      return assignProcess(id, TYPE.PROJECT);
    },

    jobRemoveAssign(id) {
      return removeAssignProcess(id, TYPE.JOB);
    },
    projectRemoveAssign(id) {
      return removeAssignProcess(id, TYPE.PROJECT);
    },

    jobRemove(id) {
      return removeProcess(id, TYPE.JOB);
    },
    projectRemove(id) {
      return removeProcess(id, TYPE.PROJECT);
    },

    jobViewApplicants(id) {
      viewProcessApplicants(id, TYPE.JOB);
    },
    projectViewApplicants(id) {
      viewProcessApplicants(id, TYPE.PROJECT);
    },
  };

  return <WrappedComponent {...suppProps} />;
};

export default withProcessActions;
